import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { FileText, Video, Share2, Sparkles, Play, Save, Calendar, Clock, Plus, X, Search, TrendingUp, User, Zap, RefreshCw, Link as LinkIcon, Crown, ExternalLink } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { prebuiltPersonas, mockTrendingTopics, mockSearchData } from '../mockAutomation';
import { freeConnectors, paidConnectors } from '../connectors';
import axios from 'axios';
import { PRICING, formatPrice } from '../constants/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Automation = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState(['']);
  const [selectedPersona, setSelectedPersona] = useState('');
  const [customPersona, setCustomPersona] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState('weekly');
  const [googleSearchEnabled, setGoogleSearchEnabled] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [wordCount, setWordCount] = useState('medium'); // short, medium, long
  const { toast } = useToast();
  const { user, isPaid } = useAuth();

  const templates = [
    {
      id: 'blog',
      name: 'Blog Post Generator',
      icon: FileText,
      description: 'Generate SEO-optimized blog posts with real-time data',
      color: 'blue',
      fields: [
        { name: 'topic', label: 'Blog Topic', type: 'input', placeholder: 'e.g., Future of AI in Business' },
        { name: 'tone', label: 'Writing Tone', type: 'select', options: ['Professional', 'Casual', 'Technical', 'Conversational'] },
        { name: 'length', label: 'Word Count', type: 'select', options: ['500', '1000', '1500', '2000'] },
        { name: 'keywords', label: 'SEO Keywords', type: 'input', placeholder: 'AI, automation, business' }
      ]
    },
    {
      id: 'youtube',
      name: 'YouTube Script Creator',
      icon: Video,
      description: 'Create engaging video scripts',
      color: 'red',
      fields: [
        { name: 'videoTopic', label: 'Video Topic', type: 'input', placeholder: 'e.g., How to use AI for marketing' },
        { name: 'duration', label: 'Video Duration', type: 'select', options: ['5 min', '10 min', '15 min', '20 min'] },
        { name: 'style', label: 'Video Style', type: 'select', options: ['Tutorial', 'Explainer', 'Review', 'Vlog'] },
        { name: 'audience', label: 'Target Audience', type: 'input', placeholder: 'e.g., Small business owners' }
      ]
    },
    {
      id: 'social',
      name: 'Social Media Posts',
      icon: Share2,
      description: 'Generate posts for multiple platforms',
      color: 'green',
      fields: [
        { name: 'message', label: 'Main Message', type: 'textarea', placeholder: 'What do you want to communicate?' },
        { name: 'platform', label: 'Platform', type: 'select', options: ['Twitter/X', 'LinkedIn', 'Facebook', 'Instagram'] },
        { name: 'callToAction', label: 'Call to Action', type: 'input', placeholder: 'e.g., Learn more, Sign up' }
      ]
    }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData({});
    setGeneratedContent('');
    setTopics(['']);
    setSearchData(null);
    
    // Load data from chat if available
    const blogData = localStorage.getItem('blogData');
    const videoData = localStorage.getItem('videoData');
    const socialData = localStorage.getItem('socialData');
    
    if (template.id === 'blog' && blogData) {
      try {
        const data = JSON.parse(blogData);
        setTopics([data.topic || '']);
        setFormData({ topic: data.topic });
        localStorage.removeItem('blogData');
        toast({ title: 'AI suggestion loaded!', description: 'Pre-filled from your chat' });
      } catch (e) {}
    } else if (template.id === 'youtube' && videoData) {
      try {
        const data = JSON.parse(videoData);
        setFormData({ videoTopic: data.topic });
        localStorage.removeItem('videoData');
        toast({ title: 'AI suggestion loaded!', description: 'Pre-filled from your chat' });
      } catch (e) {}
    } else if (template.id === 'social' && socialData) {
      try {
        const data = JSON.parse(socialData);
        setFormData({ message: data.message });
        localStorage.removeItem('socialData');
        toast({ title: 'AI suggestion loaded!', description: 'Pre-filled from your chat' });
      } catch (e) {}
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const addTopic = () => {
    setTopics(prev => [...prev, '']);
  };

  const removeTopic = (index) => {
    setTopics(prev => prev.filter((_, i) => i !== index));
  };

  const updateTopic = (index, value) => {
    setTopics(prev => prev.map((topic, i) => i === index ? value : topic));
  };

  const fetchTrendingTopics = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai/automation/trending`, {
        query: topics[0] || 'small business',
        is_paid_user: isPaid()
      });
      if (response.data.success) {
        const raw = response.data.topics;
        setTrendingTopics(raw.map(t => ({ query: t.query, trend: t.trend, category: t.category })));
        const source = response.data.source;
        toast({
          title: source === 'searxng' ? '🔴 Live Search Results' : '💡 Example Topics',
          description: source === 'searxng'
            ? 'Real-time results from our search engine'
            : 'Upgrade to PRO for live trending data',
        });
      }
    } catch {
      setTrendingTopics(mockTrendingTopics);
      toast({ title: 'Example Topics', description: 'Showing curated example topics' });
    }
  };

  const fetchSearchData = () => {
    setSearchData(mockSearchData);
    toast({
      title: 'Search Data Loaded',
      description: 'Sample search insights — PRO members get live data',
    });
  };

  const handleGenerate = async () => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to generate content.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    
    try {
      const persona = selectedPersona || customPersona || 'Professional';
      const personaInfo = prebuiltPersonas.find(p => p.id === selectedPersona);
      const personaTone = personaInfo ? personaInfo.tone : persona;
      
      let topic = '';
      let keywords = [];
      
      if (selectedTemplate.id === 'blog') {
        const validTopics = topics.filter(t => t.trim());
        topic = validTopics.length > 1 ? validTopics.join(', ') : (validTopics[0] || formData.topic || 'Your Topic');
        keywords = formData.keywords ? formData.keywords.split(',').map(k => k.trim()) : [];
      } else if (selectedTemplate.id === 'youtube') {
        topic = formData.videoTopic || 'Your Video Topic';
      } else if (selectedTemplate.id === 'social') {
        topic = formData.message || 'Your Social Media Message';
      }

      // Call backend API
      const response = await axios.post(`${BACKEND_URL}/api/ai/automation/generate`, {
        template_type: selectedTemplate.id,
        topic: topic,
        user_id: user.id,
        is_paid_user: isPaid(),
        keywords: keywords,
        tone: personaTone.toLowerCase(),
        word_count: wordCount
      });

      if (response.data.success) {
        let content = response.data.content;
        
        // Add metadata to the content
        const metadata = `**Generated with:** ${response.data.model_used}\n` +
          `**Persona:** ${personaInfo ? personaInfo.emoji + ' ' + personaInfo.name : persona} (${personaTone})\n` +
          `**Type:** ${selectedTemplate.name}\n\n`;
        
        content = metadata + content;
        
        if (scheduleEnabled) {
          content += `\n\n---\n📅 **Scheduled:** ${scheduleDate} at ${scheduleTime}${recurringEnabled ? ` (${recurringFrequency})` : ''}`;
        }
        
        setGeneratedContent(content);
        
        toast({
          title: 'Content Generated!',
          description: `Created with ${response.data.model_used} using ${personaInfo ? personaInfo.name : persona} persona`,
        });
      }
    } catch (error) {
      console.error('Content generation error:', error);
      toast({
        title: 'Generation Failed',
        description: error.response?.data?.detail || 'Failed to generate content. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    const draft = {
      id: Date.now(),
      type: selectedTemplate?.id,
      content: generatedContent,
      savedAt: new Date().toISOString(),
      scheduledFor: scheduleEnabled ? `${scheduleDate} ${scheduleTime}` : null,
    };
    const existing = JSON.parse(localStorage.getItem('globalnow_drafts') || '[]');
    existing.unshift(draft);
    localStorage.setItem('globalnow_drafts', JSON.stringify(existing.slice(0, 20))); // keep last 20
    toast({
      title: 'Saved to Drafts',
      description: scheduleEnabled
        ? `Saved with schedule note: ${scheduleDate} at ${scheduleTime}`
        : 'Saved — reload the page to see saved drafts',
    });
  };

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Content Automation Studio</h1>
          <p className="text-gray-400">AI-powered content with real-time data, personas & scheduling</p>
        </div>

        {!selectedTemplate ? (
          /* Template Selection */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card 
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className="bg-gray-800/50 border-gray-700 p-6 cursor-pointer hover:bg-gray-800/70 transition-all duration-300 hover:scale-105"
              >
                <div className={`w-12 h-12 bg-${template.color}-500/20 rounded-lg flex items-center justify-center mb-4`}>
                  <template.icon className={`w-6 h-6 text-${template.color}-400`} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{template.name}</h3>
                <p className="text-gray-400 text-sm">{template.description}</p>
              </Card>
            ))}
          </div>
        ) : (
          /* Enhanced Template Form */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Input Form */}
            <div className="space-y-6">
              <Card className="bg-gray-800/50 border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <selectedTemplate.icon className="w-6 h-6" />
                    {selectedTemplate.name}
                  </h2>
                  <Button 
                    variant="ghost" 
                    onClick={() => setSelectedTemplate(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    Change
                  </Button>
                </div>

                <div className="space-y-4">
                  {selectedTemplate.fields.map((field) => (
                    <div key={field.name}>
                      <Label className="text-gray-300 mb-2 block">{field.label}</Label>
                      {field.type === 'input' && (
                        <Input
                          placeholder={field.placeholder}
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          className="bg-gray-900 border-gray-700 text-white"
                        />
                      )}
                      {field.type === 'textarea' && (
                        <Textarea
                          placeholder={field.placeholder}
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          className="bg-gray-900 border-gray-700 text-white min-h-[100px]"
                        />
                      )}
                      {field.type === 'select' && (
                        <Select 
                          value={formData[field.name]} 
                          onValueChange={(value) => handleInputChange(field.name, value)}
                        >
                          <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                            <SelectValue placeholder={`Select ${field.label}`} />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-gray-700">
                            {field.options.map((option) => (
                              <SelectItem key={option} value={option} className="text-white">
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Multi-Topic Section */}
              {selectedTemplate.id === 'blog' && (
                <Card className="bg-gray-800/50 border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold text-white">Multiple Topics</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">Add multiple topics — AI combines them into one structured post with a section per topic.</p>
                  <div>
                  
                  {topics.map((topic, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        placeholder={`Topic ${index + 1}`}
                        value={topic}
                        onChange={(e) => updateTopic(index, e.target.value)}
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                      {topics.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeTopic(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button 
                    onClick={addTopic}
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 border-gray-700 text-gray-300"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Topic
                  </Button>
                  </div>
                </Card>
              )}

              {/* Persona Selection */}
              <Card className="bg-gray-800/50 border-gray-700 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-semibold text-white">Writing Persona</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {prebuiltPersonas.slice(0, 6).map((persona) => (
                    <Button
                      key={persona.id}
                      variant={selectedPersona === persona.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setSelectedPersona(persona.id);
                        setCustomPersona('');
                      }}
                      className={selectedPersona === persona.id ? 'bg-purple-600' : 'border-gray-700 text-gray-300'}
                    >
                      {persona.emoji} {persona.name}
                    </Button>
                  ))}
                </div>
                
                <Label className="text-gray-400 text-xs mb-2 block">Or create custom persona:</Label>
                <Textarea
                  placeholder="Describe your ideal writer (e.g., 'Expert software engineer who explains complex topics simply')"
                  value={customPersona}
                  onChange={(e) => {
                    setCustomPersona(e.target.value);
                    setSelectedPersona('');
                  }}
                  className="bg-gray-900 border-gray-700 text-white text-sm"
                  rows={2}
                />
              </Card>

              {/* Word Count Selection */}
              <Card className="bg-gray-800/50 border-gray-700 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-semibold text-white">Content Length</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant={wordCount === 'short' ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWordCount('short')}
                      className={wordCount === 'short' ? 'bg-blue-600' : 'border-gray-700 text-gray-300'}
                    >
                      Short
                    </Button>
                    <Button
                      variant={wordCount === 'medium' ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWordCount('medium')}
                      className={wordCount === 'medium' ? 'bg-blue-600' : 'border-gray-700 text-gray-300'}
                    >
                      Medium
                    </Button>
                    <Button
                      variant={wordCount === 'long' ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWordCount('long')}
                      className={wordCount === 'long' ? 'bg-blue-600' : 'border-gray-700 text-gray-300'}
                    >
                      Long
                    </Button>
                  </div>
                  
                  <div className="text-xs text-gray-400 space-y-1">
                    {selectedTemplate.id === 'blog' && (
                      <>
                        <p>• <span className="text-white">Short:</span> 300-500 words (quick read, 2-3 min)</p>
                        <p>• <span className="text-white">Medium:</span> 800-1200 words (standard blog, 5-7 min)</p>
                        <p>• <span className="text-white">Long:</span> 1500-2500 words (in-depth article, 10-15 min)</p>
                      </>
                    )}
                    {selectedTemplate.id === 'youtube' && (
                      <>
                        <p>• <span className="text-white">Short:</span> 5-7 min video script</p>
                        <p>• <span className="text-white">Medium:</span> 10-15 min video script</p>
                        <p>• <span className="text-white">Long:</span> 20-30 min video script</p>
                      </>
                    )}
                    {selectedTemplate.id === 'social' && (
                      <>
                        <p>• <span className="text-white">Short:</span> Twitter/X optimized (~280 chars)</p>
                        <p>• <span className="text-white">Medium:</span> LinkedIn/Facebook (~500 chars)</p>
                        <p>• <span className="text-white">Long:</span> Instagram caption (~1000 chars)</p>
                      </>
                    )}
                  </div>
                </div>
              </Card>

              {/* Google Search Integration */}
              {selectedTemplate.id === 'blog' && (
                <Card className="bg-gray-800/50 border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Search className="w-5 h-5 text-green-400" />
                      <h3 className="text-lg font-semibold text-white">
                        Topic Ideas {isPaid() ? <span className="text-xs text-green-400 font-normal ml-1">● Live Search</span> : <span className="text-xs text-gray-500 font-normal ml-1">· Examples (PRO = live)</span>}
                      </h3>
                    </div>
                    <Switch 
                      checked={googleSearchEnabled}
                      onCheckedChange={setGoogleSearchEnabled}
                    />
                  </div>
                  
                  {googleSearchEnabled && (
                    <div className="space-y-3">
                      <Button
                        onClick={fetchTrendingTopics}
                        variant="outline"
                        size="sm"
                        className="w-full border-gray-700 text-gray-300"
                      >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        {isPaid() ? 'Load Live Topic Ideas' : 'Load Example Topics'}
                      </Button>
                      
                      <Button 
                        onClick={fetchSearchData}
                        variant="outline"
                        size="sm"
                        className="w-full border-gray-700 text-gray-300"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Fetch Search Insights
                      </Button>
                      
                      {trendingTopics.length > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto">
                          <Label className="text-gray-400 text-xs mb-2 block">Trending Now:</Label>
                          {trendingTopics.slice(0, 4).map((topic, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1 px-2 hover:bg-gray-700/50 rounded cursor-pointer" onClick={() => updateTopic(0, topic.query)}>
                              <span className="text-sm text-gray-300">{topic.query}</span>
                              <Badge className="bg-green-500/20 text-green-400 text-xs">{topic.trend}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {/* Scheduling */}
              <Card className="bg-gray-800/50 border-gray-700 p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">Schedule Note</h3>
                  </div>
                  <Switch
                    checked={scheduleEnabled}
                    onCheckedChange={setScheduleEnabled}
                  />
                </div>
                
                <p className="text-xs text-gray-500 mb-3">
                  Add a date/time note to your content — copy it to your social platform's scheduler to publish.
                </p>

                {scheduleEnabled && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-gray-400 text-xs mb-1 block">Date</Label>
                        <Input
                          type="date"
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                          className="bg-gray-900 border-gray-700 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-400 text-xs mb-1 block">Time</Label>
                        <Input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="bg-gray-900 border-gray-700 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                      <Label className="text-gray-400 text-sm">Recurring</Label>
                      <Switch 
                        checked={recurringEnabled}
                        onCheckedChange={setRecurringEnabled}
                      />
                    </div>
                    
                    {recurringEnabled && (
                      <Select value={recurringFrequency} onValueChange={setRecurringFrequency}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          <SelectItem value="daily" className="text-white">Daily</SelectItem>
                          <SelectItem value="weekly" className="text-white">Weekly</SelectItem>
                          <SelectItem value="biweekly" className="text-white">Bi-weekly</SelectItem>
                          <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </Card>

              <Button 
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                {loading ? (
                  <><Sparkles className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Zap className="w-5 h-5 mr-2" /> Generate Content</>
                )}
              </Button>
            </div>

            {/* Right Column - Output Preview */}
            <Card className="bg-gray-800/50 border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Generated Content</h2>
                {generatedContent && (
                  <Button 
                    onClick={handleSave}
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                )}
              </div>

              {!generatedContent ? (
                <div className="text-center py-20 text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Configure your settings and click generate to see AI-powered content here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <Textarea
                    value={generatedContent}
                    onChange={(e) => setGeneratedContent(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white min-h-[500px] font-mono text-sm"
                  />
                  
                  {scheduleEnabled && (
                    <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <Calendar className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-blue-300">
                        Scheduled for {scheduleDate} at {scheduleTime}
                        {recurringEnabled && ` (${recurringFrequency})`}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Connectors Section - Show after content is generated */}
        {generatedContent && (
          <Card className="bg-gray-800/50 border-gray-700 p-6 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <LinkIcon className="w-6 h-6 text-blue-400" />
              <h2 className="text-2xl font-bold text-white">Export & Connect</h2>
            </div>
            <p className="text-gray-400 mb-6">
              Send your generated content to these platforms automatically
            </p>

            {/* Free Connectors */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Badge className="bg-green-500/20 text-green-400">FREE</Badge>
                Available Connectors
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {freeConnectors.map(connector => (
                  <Button
                    key={connector.id}
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-800 justify-start h-auto py-3"
                    onClick={() => {
                      toast({
                        title: `${connector.name} Integration`,
                        description: 'Opening connector setup...',
                      });
                      window.open(connector.setupUrl, '_blank');
                    }}
                  >
                    <span className="text-2xl mr-3">{connector.icon}</span>
                    <div className="text-left">
                      <div className="font-semibold">{connector.name}</div>
                      <div className="text-xs text-gray-500">{connector.description}</div>
                    </div>
                    <ExternalLink className="w-4 h-4 ml-auto" />
                  </Button>
                ))}
              </div>
            </div>

            {/* Paid Connectors */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Badge className="bg-yellow-500/20 text-yellow-400">
                  <Crown className="w-3 h-3 mr-1" />
                  Coming Soon
                </Badge>
                Native Integrations
              </h3>
              <p className="text-gray-500 text-xs mb-4">
                Direct connections to these platforms are on our roadmap — arriving in a future update for PRO members.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {paidConnectors.map(connector => (
                  <div
                    key={connector.id}
                    className="border border-gray-700/50 rounded-lg p-3 flex items-center gap-3 opacity-60"
                  >
                    <span className="text-xl">{connector.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-300">{connector.name}</div>
                      <div className="text-xs text-gray-600 truncate">{connector.description}</div>
                    </div>
                    <Badge className="bg-gray-700/50 text-gray-500 text-xs border-0 shrink-0">Soon</Badge>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Automation;